import { useState } from 'react';

export const SUPPLY_PAGE_SIZES = [10, 20, 50] as const;
export const DEFAULT_SUPPLY_PAGE_SIZE = 20;

export interface SupplyPaginationState<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  start: number;
  end: number;
}

export interface SupplyPaginationResult<T> extends SupplyPaginationState<T> {
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

const validPageSize = (value: number) => SUPPLY_PAGE_SIZES.some(size => size === value);

/** Slice the supplied order without changing the original array or its items. */
export function paginateSupplyItems<T>(items: T[], requestedPage = 1, requestedPageSize = DEFAULT_SUPPLY_PAGE_SIZE): SupplyPaginationState<T> {
  const pageSize = validPageSize(requestedPageSize) ? requestedPageSize : DEFAULT_SUPPLY_PAGE_SIZE;
  const total = items.length;
  const totalPages = Math.ceil(total / pageSize);
  const page = Math.min(Math.max(1, Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1), Math.max(1, totalPages));
  const offset = (page - 1) * pageSize;
  return {
    items: items.slice(offset, offset + pageSize),
    page, pageSize, total, totalPages,
    start: total ? offset + 1 : 0,
    end: Math.min(offset + pageSize, total),
  };
}

/** Keep pagination local to a filter/search/access scope, never to a cached key. */
export function useSupplyPagination<T>(items: T[], resetKey: string): SupplyPaginationResult<T> {
  const [state, setState] = useState({ resetKey, page: 1, pageSize: DEFAULT_SUPPLY_PAGE_SIZE });
  const pagination = paginateSupplyItems(items, state.resetKey === resetKey ? state.page : 1, state.pageSize);

  // Synchronize during this component's render, not in an effect: children must
  // never receive an old filter's page, and a removed page must not reappear on
  // a later refresh. React retries this render before committing its children.
  if (state.resetKey !== resetKey || state.page !== pagination.page) {
    setState({ ...state, resetKey, page: pagination.page });
  }

  const setPage = (requestedPage: number) => {
    setState(current => {
      const page = paginateSupplyItems(items, requestedPage, current.pageSize).page;
      return current.resetKey === resetKey && current.page === page ? current : { ...current, resetKey, page };
    });
  };
  const setPageSize = (pageSize: number) => {
    if (!validPageSize(pageSize)) return;
    setState(current => current.resetKey === resetKey && current.pageSize === pageSize
      ? current : { resetKey, page: 1, pageSize });
  };

  return { ...pagination, setPage, setPageSize };
}
